import { Link } from "react-router-dom";
import {
  Sprout, Scissors, TreePalm, Layers, ShieldCheck, FlaskConical,
  Grid3x3, Compass, Droplets, Leaf, Lightbulb, Mountain, Droplet, Phone, ArrowRight,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { BRAND, SERVICES } from "../lib/brand";

const ICONS = {
  Sprout, Scissors, TreePalm, Layers, ShieldCheck, FlaskConical,
  Grid3x3, Compass, Droplets, Leaf, Lightbulb, Mountain, Droplet,
};

export default function Services() {
  return (
    <div data-testid="services-page">
      {/* HERO */}
      <section
        data-testid="services-hero"
        className="relative py-28 bg-[#0F1A14] text-white overflow-hidden"
      >
        <div className="absolute inset-0 opacity-30">
          <img
            src="https://images.unsplash.com/photo-1596552254354-3ba557926c98?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwxfHxsYXduJTIwbW93ZXIlMjBmcmVzaCUyMGN1dCUyMGdyYXNzfGVufDB8fHx8MTc3ODI2ODI4OHww&ixlib=rb-4.1.0&q=85"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="section-divider mb-6" />
          <p className="uppercase tracking-[0.2em] text-xs text-[#86EFAC] mb-3 font-body">Services</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight max-w-3xl">
            Expert lawn care &amp; landscaping services.
          </h1>
          <p className="mt-6 text-gray-200 max-w-2xl font-body text-lg">
            Everything your South Florida property needs to look its best —
            handled by a single, dependable team.
          </p>
        </div>
      </section>

      {/* SERVICES GRID */}
      <section data-testid="services-grid-section" className="py-24 leaf-pattern">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {SERVICES.map((s) => {
              const Icon = ICONS[s.icon] || Leaf;
              return (
                <article
                  key={s.slug}
                  data-testid={`service-card-${s.slug}`}
                  className="group relative bg-white rounded-2xl border border-gray-200 overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:border-[#15803D] hover:shadow-[0_24px_50px_-20px_rgba(21,128,61,0.35)]"
                >
                  <div className="relative h-52 overflow-hidden">
                    <img
                      src={s.image}
                      alt={s.title}
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent" />
                    <div className="absolute top-4 left-4 h-11 w-11 rounded-xl bg-white/95 backdrop-blur grid place-items-center text-[#15803D] shadow-md">
                      <Icon className="h-5 w-5" />
                    </div>
                    <h3 className="absolute bottom-4 left-4 right-4 font-display text-xl font-semibold text-white drop-shadow">
                      {s.title}
                    </h3>
                  </div>
                  <div className="p-6">
                    <p className="text-sm text-gray-600 font-body leading-relaxed">
                      {s.description}
                    </p>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section data-testid="services-process" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <div className="section-divider mb-6 mx-auto" />
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-gray-900">
              How working with us looks
            </h2>
            <p className="mt-3 text-gray-600 font-body">
              Simple, transparent, and built around your schedule.
            </p>
          </div>

          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {[
              { step: "01", title: "Free Quote", body: "Call or send the form. We'll discuss your needs and visit if needed." },
              { step: "02", title: "Custom Plan", body: "We design a service plan that fits your property and budget." },
              { step: "03", title: "Consistent Care", body: "Your crew shows up on schedule and your yard looks great — always." },
            ].map((p) => (
              <div
                key={p.step}
                data-testid={`process-step-${p.step}`}
                className="bg-white rounded-2xl border border-gray-200 p-7"
              >
                <div className="font-display text-5xl font-bold text-[#DC2626] leading-none">
                  {p.step}
                </div>
                <h3 className="mt-4 font-display text-xl font-semibold text-gray-900">
                  {p.title}
                </h3>
                <p className="mt-2 text-sm text-gray-600 font-body leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section
        data-testid="services-cta"
        className="py-20 bg-[#15803D] text-white"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid gap-8 md:grid-cols-2 items-center">
          <div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">
              Need a service we didn't list?
            </h2>
            <p className="mt-4 text-white/90 font-body max-w-lg">
              Reach out — chances are, we can help. {BRAND.experienceYears}+
              years on Florida properties means we've seen (and handled) it all.
            </p>
          </div>
          <div className="flex flex-wrap gap-4 md:justify-end">
            <Button
              asChild
              size="lg"
              data-testid="services-cta-quote"
              className="bg-[#DC2626] hover:bg-red-700 text-white rounded-full px-7 h-12"
            >
              <Link to="/contact">
                Request a Quote
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              data-testid="services-cta-call"
              className="bg-white text-[#15803D] border-white hover:bg-white/90 rounded-full px-7 h-12"
            >
              <a href={`tel:${BRAND.phoneTel}`}>
                <Phone className="mr-2 h-4 w-4" />
                {BRAND.phone}
              </a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
